import {
  CircleAlert,
  HelpCircle,
  ImageOff,
  Mountain,
  MoreHorizontal,
  ShieldAlert,
  SpellCheck2,
  type LucideIcon,
} from "lucide-react";

/**
 * Why a learner is reporting a question.
 *
 * Mirrors `ExerciseFlagger::REPORT_REASONS`. The keys are code: they are written
 * into the flag, read back by `/admin/flagged`, and an unknown one is **rejected**
 * server-side rather than filed under „other", because a miscategorised report is
 * worse than none. `spec/services/exercise_flagger_spec.rb` reads this file and
 * fails if the two lists drift — a reason added on one side only is a button that
 * errors when a child presses it.
 *
 * One list, two readers: the child's form (`report-problem.tsx`) and the
 * grown-up's queue (`FlaggedContentPage.tsx`). They used to hold a copy each, so
 * a new reason meant a chip the admin page rendered as „Mokinio pranešimas".
 */
export type ReportReason = {
  /** Code. Never change one — stored flags point at it. */
  key: string;
  /** What the chip says, in the child's voice. */
  label: string;
  icon: LucideIcon;
  /**
   * The question put to the child once this reason is picked. „Gali parašyti
   * plačiau" is a blank page; „Kurį žodį reikėtų pataisyti?" is answerable in
   * four words, and four words is what makes the report actionable.
   */
  prompt: string;
  /**
   * Whether the words are required. Only „Kita" is — it names nothing on its
   * own, so an admin opening it learns exactly what they knew before.
   */
  needsComment?: boolean;
};

export const REPORT_REASONS: readonly ReportReason[] = [
  {
    key: "unclear",
    label: "Nesupratau, ko klausiama",
    icon: HelpCircle,
    prompt: "Kuri vieta buvo neaiški?",
  },
  {
    key: "grammar",
    label: "Yra rašybos ar gramatikos klaida",
    icon: SpellCheck2,
    prompt: "Kurį žodį reikėtų pataisyti?",
  },
  {
    key: "wrong",
    label: "Užduotyje yra klaida",
    icon: CircleAlert,
    prompt: "Kas užduotyje neteisinga?",
  },
  {
    key: "image",
    label: "Nesimato paveikslėlio",
    icon: ImageOff,
    prompt: "Kas matosi toje vietoje, kur turėtų būti paveikslėlis?",
  },
  {
    key: "too_hard",
    label: "Per sunki — dar to nesimokėme",
    icon: Mountain,
    prompt: "Ko tau čia labiausiai neužteko?",
  },
  {
    key: "other",
    label: "Kita",
    icon: MoreHorizontal,
    prompt: "Parašyk, kas negerai",
    needsComment: true,
  },
] as const;

/**
 * Why somebody is reporting a message the AI tutor wrote — a different author,
 * so a different list. Mirrors `ExerciseFlagger::AI_MESSAGE_REPORT_REASONS` and
 * is held to it by the same spec. The comment is optional for all three: one
 * press on a reason is already a report a grown-up can act on, and this is the
 * button for a child who has just read something they should not have.
 */
export const AI_MESSAGE_REPORT_REASONS: readonly ReportReason[] = [
  {
    key: "inappropriate",
    label: "Netinkamas turinys",
    icon: ShieldAlert,
    prompt: "Kas buvo netinkama?",
  },
  {
    key: "wrong",
    label: "Neteisingas atsakymas",
    icon: CircleAlert,
    prompt: "Kas čia neteisinga?",
  },
  {
    key: "other",
    label: "Kita",
    icon: MoreHorizontal,
    prompt: "Parašyk, kas negerai",
  },
] as const;

export function aiMessageReportReasonLabel(key: string | null | undefined): string | undefined {
  return AI_MESSAGE_REPORT_REASONS.find((r) => r.key === key)?.label;
}

/** Matches `Mutations::ReportExerciseProblem::MAX_COMMENT`. */
export const MAX_REPORT_COMMENT = 500;

export function reportReason(key: string | null | undefined): ReportReason | undefined {
  return REPORT_REASONS.find((r) => r.key === key);
}

/** The chip's words for a stored flag; „—" is never shown, the caller decides. */
export function reportReasonLabel(key: string | null | undefined): string | undefined {
  return reportReason(key)?.label;
}
